import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { BookOpen, Shuffle, Trophy, Check, X, ArrowRight, Brain, Lightbulb, Star, RefreshCw } from "lucide-react";

export default function MemoryVerseGame({ childId, childName, onClose }) {
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    loadGame();
  }, [childId]);

  const loadGame = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/memory-verse/practice/${childId}`);
      setGameData(data);
      setActiveGame(null);
      setFeedback(null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-10 h-10 border-4 border-[#9B5DE5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!gameData) return null;

  const { verse_text, verse_reference, theme, progress, games } = gameData;
  const gameIcons = {
    fill_blank: { icon: Lightbulb, color: "#06D6A0", label: "Fill the Blanks" },
    word_scramble: { icon: Shuffle, color: "#F77F00", label: "Word Scramble" },
    first_letter: { icon: Brain, color: "#9B5DE5", label: "First Letter Hints" },
    full_verse: { icon: Trophy, color: "#EF476F", label: "Full Recall" },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="memory-verse-game">
        {/* Header */}
        <div className="sticky top-0 z-10 p-4" style={{ background: "linear-gradient(135deg, #9B5DE5, #118AB2)" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen size={22} color="white" />
              <div>
                <p className="text-white font-black text-lg" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  Verse Practice
                </p>
                <p className="text-white/70 text-xs font-medium">{theme} - {verse_reference}</p>
              </div>
            </div>
            <button onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-xl p-2 transition-all">
              <X size={20} color="white" />
            </button>
          </div>
          {/* Progress */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all"
                style={{ width: `${progress.accuracy}%` }} />
            </div>
            <span className="text-white/80 text-xs font-bold">
              {progress.correct}/{progress.attempts} correct
            </span>
            {progress.mastered && (
              <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">Mastered</span>
            )}
          </div>
        </div>

        {/* Verse display */}
        <div className="p-4">
          <div className="bg-purple-50 rounded-2xl p-4 mb-4 border-l-4 border-[#9B5DE5]">
            <p className="text-gray-800 font-medium italic leading-relaxed">"{verse_text}"</p>
            <p className="text-sm text-purple-600 font-bold mt-2">— {verse_reference}</p>
          </div>

          {feedback && (
            <div className={`rounded-2xl p-4 mb-4 ${feedback.correct ? "bg-green-50 border border-green-200" : "bg-orange-50 border border-orange-200"}`}>
              <div className="flex items-center gap-2 mb-1">
                {feedback.correct ? <Check size={18} color="#06D6A0" /> : <RefreshCw size={18} color="#F77F00" />}
                <span className={`font-bold text-sm ${feedback.correct ? "text-green-700" : "text-orange-700"}`}>
                  {feedback.feedback}
                </span>
              </div>
              {feedback.badge_earned && (
                <div className="flex items-center gap-2 mt-2 bg-yellow-50 rounded-xl p-2 border border-yellow-200">
                  <Star size={16} color="#F77F00" fill="#F77F00" />
                  <span className="text-sm font-bold text-yellow-800">
                    Badge: {feedback.badge_earned.name}!
                  </span>
                </div>
              )}
              <button onClick={loadGame}
                className="mt-3 w-full py-2 rounded-xl bg-[#9B5DE5] text-white font-bold text-sm hover:scale-[1.02] active:scale-[0.98] transition-all">
                Try Again
              </button>
            </div>
          )}

          {/* Game selection or active game */}
          {!activeGame && !feedback ? (
            <div className="space-y-3">
              <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Choose a Game</p>
              {games.map((game) => {
                const meta = gameIcons[game.type] || gameIcons.fill_blank;
                const Icon = meta.icon;
                return (
                  <button key={game.type}
                    onClick={() => setActiveGame(game)}
                    className="w-full p-4 rounded-2xl border-2 border-gray-100 hover:border-purple-200 hover:bg-gray-50 transition-all text-left flex items-center gap-3"
                    data-testid={`game-${game.type}`}
                  >
                    <div className="rounded-xl p-2.5" style={{ background: meta.color + "18" }}>
                      <Icon size={22} color={meta.color} strokeWidth={2.5} />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-800">{meta.label}</p>
                      <p className="text-xs text-gray-500">{game.instruction}</p>
                    </div>
                    <span className="text-xs font-bold px-2 py-1 rounded-full"
                      style={{ background: meta.color + "18", color: meta.color }}>
                      {game.difficulty}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : activeGame && !feedback ? (
            <GamePlayer
              game={activeGame}
              childId={childId}
              verseText={verse_text}
              onResult={(result) => setFeedback(result)}
              onBack={() => setActiveGame(null)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function GamePlayer({ game, childId, verseText, onResult, onBack }) {
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [scrambleAnswers, setScrambleAnswers] = useState({});

  const submit = async (gameType, ans) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/memory-verse/practice/submit`, {
        child_id: childId,
        answer: ans || answer,
        game_type: gameType || game.type,
      });
      onResult(data);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (game.type === "fill_blank") {
    return (
      <FillBlankGame
        game={game}
        onSubmit={() => submit("fill_blank", answer)}
        onBack={onBack}
        submitting={submitting}
      />
    );
  }

  if (game.type === "word_scramble") {
    return (
      <WordScrambleGame
        game={game}
        answers={scrambleAnswers}
        setAnswers={setScrambleAnswers}
        onSubmit={() => submit("word_scramble", Object.values(scrambleAnswers).join(","))}
        onBack={onBack}
        submitting={submitting}
      />
    );
  }

  // first_letter and full_verse: type the verse
  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-purple-600 font-bold hover:underline">
        &larr; Back to games
      </button>
      <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
        <p className="text-sm font-bold text-blue-800 mb-2">{game.instruction}</p>
        {game.type === "first_letter" && (
          <p className="font-mono text-lg text-gray-600 leading-relaxed mb-3">{game.hints}</p>
        )}
        {game.type === "full_verse" && (
          <p className="text-sm text-gray-500 mb-3">
            Hint: Starts with "<span className="font-bold">{game.first_word_hint}</span>..." ({game.word_count} words)
          </p>
        )}
      </div>
      <textarea
        value={answer}
        onChange={e => setAnswer(e.target.value)}
        rows={4}
        placeholder="Type the verse here..."
        className="w-full rounded-xl border-2 border-gray-200 focus:border-[#9B5DE5] p-3 text-sm font-medium outline-none transition-all resize-none"
        data-testid="verse-input"
      />
      <button
        onClick={() => submit(game.type, answer)}
        disabled={!answer.trim() || submitting}
        className="w-full py-3 rounded-xl font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
        style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}
        data-testid="submit-verse-btn"
      >
        {submitting ? "Checking..." : "Check My Answer"}
        <ArrowRight size={18} />
      </button>
    </div>
  );
}

function FillBlankGame({ game, onSubmit, onBack, submitting }) {
  const [filled, setFilled] = useState({});
  const wordBank = [...game.word_bank].sort(() => Math.random() - 0.5);
  const [available, setAvailable] = useState(wordBank);

  const placeBlanks = (index, word) => {
    setFilled(prev => ({ ...prev, [index]: word }));
    setAvailable(prev => prev.filter((_, i) => i !== prev.indexOf(word)));
  };

  const removeFill = (index) => {
    const word = filled[index];
    if (word) {
      setAvailable(prev => [...prev, word]);
      setFilled(prev => { const n = { ...prev }; delete n[index]; return n; });
    }
  };

  const allFilled = game.blanks.every(b => filled[b.index]);

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-purple-600 font-bold hover:underline">
        &larr; Back to games
      </button>
      <p className="text-sm font-bold text-gray-600">{game.instruction}</p>
      <div className="bg-gray-50 rounded-2xl p-4 leading-relaxed text-gray-700 font-medium">
        {game.verse_with_blanks.split(" ").map((word, i) => {
          const blank = game.blanks.find(b => b.index === i);
          if (blank) {
            return (
              <button key={i}
                onClick={() => removeFill(i)}
                className={`inline-block mx-0.5 px-2 py-0.5 rounded-lg border-2 border-dashed min-w-[60px] text-center transition-all ${
                  filled[i] ? "border-[#06D6A0] bg-green-50 text-green-700 font-bold" : "border-gray-300 text-gray-400"
                }`}
                data-testid={`blank-${i}`}
              >
                {filled[i] || "___"}
              </button>
            );
          }
          return <span key={i} className="mx-0.5">{word}</span>;
        })}
      </div>
      {/* Word bank */}
      <div className="flex flex-wrap gap-2">
        {available.map((word, i) => (
          <button key={i}
            onClick={() => {
              const nextBlank = game.blanks.find(b => !filled[b.index]);
              if (nextBlank) placeBlanks(nextBlank.index, word);
            }}
            className="px-3 py-1.5 rounded-xl bg-purple-100 text-purple-700 font-bold text-sm hover:bg-purple-200 transition-all"
            data-testid={`word-bank-${i}`}
          >
            {word}
          </button>
        ))}
      </div>
      <button onClick={onSubmit} disabled={!allFilled || submitting}
        className="w-full py-3 rounded-xl font-bold text-white disabled:opacity-50 transition-all"
        style={{ background: "linear-gradient(90deg, #06D6A0, #118AB2)" }}
        data-testid="submit-fill-blank-btn"
      >
        {submitting ? "Checking..." : "Check Answers"}
      </button>
    </div>
  );
}

function WordScrambleGame({ game, answers, setAnswers, onSubmit, onBack, submitting }) {
  const allAnswered = game.words.every((_, i) => answers[i]?.trim());

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-purple-600 font-bold hover:underline">
        &larr; Back to games
      </button>
      <p className="text-sm font-bold text-gray-600">{game.instruction}</p>
      <div className="space-y-3">
        {game.words.map((w, i) => (
          <div key={i} className="flex items-center gap-3 bg-orange-50 rounded-xl p-3 border border-orange-100">
            <span className="font-mono text-lg font-bold text-orange-600 tracking-wider min-w-[80px] text-center">
              {w.scrambled}
            </span>
            <ArrowRight size={16} className="text-gray-400 flex-shrink-0" />
            <input
              value={answers[i] || ""}
              onChange={e => setAnswers(prev => ({ ...prev, [i]: e.target.value }))}
              placeholder="Unscramble..."
              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium outline-none focus:border-orange-400 transition-all"
              data-testid={`scramble-input-${i}`}
            />
          </div>
        ))}
      </div>
      <button onClick={onSubmit} disabled={!allAnswered || submitting}
        className="w-full py-3 rounded-xl font-bold text-white disabled:opacity-50 transition-all"
        style={{ background: "linear-gradient(90deg, #F77F00, #FFD166)" }}
        data-testid="submit-scramble-btn"
      >
        {submitting ? "Checking..." : "Check Answers"}
      </button>
    </div>
  );
}
